import { BrandPlan, BrandPlanTactic, BrandPlanIntegrations, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { getTacticsFromContentCounts } from 'screens/BrandKits/components/BrandPlan/utils'
import { getTacticInfo } from 'components/TacticIntegrationList/utils'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

/**
 * Combines tactics from brand plan and content counts, normalizing and deduplicating them.
 * InstagramReel is normalized to Instagram since they map to the same integration.
 */
export const getNormalizedTactics = (brandPlan: BrandPlan | null | undefined): BrandPlanIntegrations[] => {
  if (!brandPlan) {
    return []
  }

  const allTactics = [...brandPlan.tactics, ...getTacticsFromContentCounts(brandPlan)]

  const normalizedTactics = allTactics.map((tactic) => normalizeTactic(tactic))

  return Array.from(new Set(normalizedTactics))
}

export const normalizeTactic = (tactic: BrandPlanIntegrations): BrandPlanIntegrations => {
  return tactic === BrandPlanTactic.InstagramReel ? BrandPlanTactic.Instagram : tactic
}

export const getProviderCounts = (
  externalIntegrations: ExternalIntegration[] | undefined,
  tactics: BrandPlanIntegrations[],
  selectedBlogPlatform: SelectedBlogPlatform | undefined,
  selectedEmailPlatform: SelectedEmailPlatform | undefined,
): {
  connectedProviders: ExternalIntegrationEnum[]
  totalPossibleProviders: ExternalIntegrationEnum[]
} => {
  // Get all tactics including 'google' which is always shown
  const allTacticsForCounting: BrandPlanIntegrations[] = [...tactics, 'google']

  // Get integration types for each tactic (filtering out nulls for SEO/Email without platforms)
  const integrationTypes = allTacticsForCounting
    .map((tactic) => getTacticInfo(tactic, selectedBlogPlatform, selectedEmailPlatform))
    .filter((info): info is NonNullable<typeof info> => info !== null)
    .map((info) => info.integrationType)

  if (!externalIntegrations) {
    return {
      connectedProviders: [],
      totalPossibleProviders: Array.from(new Set(integrationTypes)),
    }
  }

  const allConnectedProviderTypes = Array.from(new Set(externalIntegrations.map((integration) => integration.provider)))
  const providersWithTactics = allConnectedProviderTypes.filter((provider) => integrationTypes.includes(provider))
  const providersWithoutTactics = allConnectedProviderTypes.filter((provider) => !integrationTypes.includes(provider))
  const allPossibleProviders = [...integrationTypes, ...providersWithoutTactics]
  const totalPossibleProviders = Array.from(new Set(allPossibleProviders))
  const connectedProviders = Array.from(new Set([...providersWithTactics, ...providersWithoutTactics]))

  return {
    connectedProviders,
    totalPossibleProviders,
  }
}
